import gql from 'graphql-tag';
import cloneDeep from 'lodash/cloneDeep';
import { augmentDrawing } from 'utils/drawings';
import { getDrawingQuery, getDrawingsQuery } from './common';
import { DRAWING_BOARD_EVENTS } from './drawingConstants';

//  ---------------------------------

export const loadMyDrawing = (client, drawingId) => {
  const data = {
    drawing: {
      data: client.readQuery({
        query: getDrawingQuery(),
        varibales: {
          input: { id: drawingId }
        }
      }),
      found: false,
      foundIdx: -1
    }
  };

  return data;
};

export const loadDrawingsFromCache = (client, drawingId) => {
  const data = {
    commonDrawings: {
      data: client.readQuery({
        query: getDrawingsQuery(),
        variables: {
          input: {
            states: ['InProgress', 'Upcoming'],
            onlyMy: false,
            enabled: true
          }
        }
      }),
      found: false,
      foundIdx: -1
    },
    myDrawings: {
      data: client.readQuery({
        query: getDrawingsQuery(),
        variables: {
          input: {
            states: ['InProgress', 'Upcoming'],
            onlyMy: true,
            enabled: true
          }
        }
      }),
      found: false,
      foundIdx: -1
    }
  };

  if (drawingId) {
    data.commonDrawings.foundIdx =
      data.commonDrawings.data.drawings.drawings.findIndex(
        d => d.id === drawingId
      );
    data.commonDrawings.found =
      data.commonDrawings.data.drawings.drawings[
        data.commonDrawings.foundIdx
      ] || false;

    data.myDrawings.foundIdx = data.myDrawings.data.drawings.drawings.findIndex(
      d => d.id === drawingId
    );
    data.myDrawings.found =
      data.myDrawings.data.drawings.drawings[data.myDrawings.foundIdx] || false;
  }

  return data;
};

//  ---------------------------------
export const findCommonDrawingInCache = (client, drawingId) => {
  const commonDrawings = client.readQuery({
    query: getDrawingsQuery(),
    variables: {
      input: {
        states: ['InProgress', 'Upcoming'],
        onlyMy: false
      }
    }
  });
  return (
    (commonDrawings &&
      commonDrawings.drawings &&
      commonDrawings.drawings.drawings &&
      commonDrawings.drawings.drawings.find(d => d.id === drawingId)) ||
    null
  );
};

//  ---------------------------------
export const findMyDrawingInCache = (client, drawingId) => {
  const myDrawings = client.readQuery({
    query: getDrawingsQuery(),
    variables: {
      input: {
        states: ['InProgress', 'Upcoming'],
        onlyMy: true
      }
    }
  });

  return (
    (myDrawings &&
      myDrawings.drawings &&
      myDrawings.drawings.drawings &&
      myDrawings.drawings.drawings.find(d => d.id === drawingId)) ||
    null
  );
};

//  ---------------------------------

export const updateDrawingsInCache = (client, data) => {
  client.writeQuery({
    query: getDrawingsQuery(),
    variables: {
      input: {
        states: ['InProgress', 'Upcoming'],
        onlyMy: false,
        enabled: true
      }
    },
    data: data.commonDrawings.data
  });
  client.writeQuery({
    query: getDrawingsQuery(),
    variables: {
      input: {
        states: ['InProgress', 'Upcoming'],
        onlyMy: true,
        enabled: true
      }
    },
    data: data.myDrawings.data
  });
};

//  ---------------------------------
export const fetchDrawing = async (client, drawingId, winners = false) => {
  let drawing = false;
  try {
    const resp = await client.query({
      query: getDrawingQuery(winners),
      variables: { input: { id: drawingId } },
      fetchPolicy: 'network-only'
    });
    drawing = (resp.data && resp.data.drawing) || false;
  } catch (e) {
    console.error(
      'fetchDrawing exception, drawingId',
      drawingId,
      'error:',
      e.toString()
    );
  }

  if (drawing) {
    augmentDrawing(drawing);
  }
  return drawing;
};

//  ---------------------------------
export const fetchMyEnteredTickets = async (client, drawingId) => {
  const QUERY_GET_BOARD = gql`
    query DrawingBoard($input: GetDrawingBoard!) {
      drawingBoard(input: $input) {
        myEnteredTickets
      }
    }
  `;
  let tickets = 0;
  try {
    const resp = await client.query({
      query: QUERY_GET_BOARD,
      variables: { input: { id: drawingId } },
      fetchPolicy: 'network-only'
    });
    tickets =
      (resp.data &&
        resp.data.drawingBoard &&
        resp.data.drawingBoard.myEnteredTickets) ||
      0;
  } catch (e) {
    console.error(
      'fetchMyEnteredTickets exception, drawingId',
      drawingId,
      'error:',
      e.toString()
    );
  }

  return tickets;
};

//  ---------------------------------
const removeDrawingFromData = data => {
  if (data.commonDrawings.found) {
    data.commonDrawings.data.drawings.drawings.splice(
      data.commonDrawings.foundIdx,
      1
    );
    if (data.myDrawings.found) {
      data.myDrawings.data.drawings.drawings.splice(
        data.myDrawings.foundIdx,
        1
      );
    }
  }
  data.commonDrawings.foundIdx = -1;
  data.commonDrawings.found = false;
  data.myDrawings.foundIdx = -1;
  data.myDrawings.found = false;
};

//  ---------------------------------

export const updateDrawings = async (client, userId, event) => {
  // console.log('+++ DEBUG cacheHandling.updateDrawings, event', event);
  const data = loadDrawingsFromCache(
    client,
    event.event === DRAWING_BOARD_EVENTS.CREATED ? null : event.drawingId
  );

  let commonDrawing = data.commonDrawings.found;
  let myDrawing = data.myDrawings.found;
  let drawing = false;
  let update = false;

  if (
    event.event === DRAWING_BOARD_EVENTS.CREATED ||
    event.event === DRAWING_BOARD_EVENTS.UPDATED
  ) {
    drawing = await fetchDrawing(client, event.drawingId);
    if (!drawing) {
      console.error(
        `updateDrawings, event [${event.event}], drawing NOT fetched`,
        event
      );
      return;
    }

    if (event.event === DRAWING_BOARD_EVENTS.CREATED) {
      data.commonDrawings.data.drawings.drawings.push(drawing);
    } else {
      if (drawing.enabled && !commonDrawing) {
        data.commonDrawings.data.drawings.drawings.push(drawing);
        const myTckets = await fetchMyEnteredTickets(client, drawing.id);
        if (myTckets) {
          data.myDrawings.data.drawings.drawings.push(drawing);
        }
      } else if (!drawing.enabled && commonDrawing) {
        //  the drawing is just disabled and is in cache
        removeDrawingFromData(data);
        commonDrawing = false;
      } else {
        update = cloneDeep(drawing);
      }
    }
  } else if (
    event.event === DRAWING_BOARD_EVENTS.END ||
    event.event === DRAWING_BOARD_EVENTS.DELETED
  ) {
    removeDrawingFromData(data);
    if (event.event === DRAWING_BOARD_EVENTS.DELETED) {
      commonDrawing.enabled = false;
      commonDrawing = false;
    } else {
      drawing = await fetchDrawing(
        client,
        event.drawingId,
        true /*with winners*/
      );
      update = cloneDeep(drawing);
    }
  } else if (
    event.event === DRAWING_BOARD_EVENTS.ADD_TICKETS ||
    event.event === DRAWING_BOARD_EVENTS.ADD_FREE_TICKET
  ) {
    if (
      event.user &&
      event.user.userId === userId
      //  tickets added by the current Player
    ) {
      if (event.event === DRAWING_BOARD_EVENTS.ADD_FREE_TICKET) {
        update = { hasEnteredFreeTicket: true };
      }

      if (!myDrawing && commonDrawing) {
        //  and drawing still not in "My Drawings" - makes copy and pushes it to myDrawings list
        myDrawing = cloneDeep(commonDrawing);
        data.myDrawings.data.drawings.drawings.push(myDrawing);
      }
    }
  } else if (event.event === DRAWING_BOARD_EVENTS.REFUNDED) {
    if (myDrawing) {
      data.myDrawings.data.drawings.drawings.splice(
        data.myDrawings.foundIdx,
        1
      );
      myDrawing = false;
    }
    update = { hasEnteredFreeTicket: false };
  } else if (event.event === DRAWING_BOARD_EVENTS.ROUND) {
    update = { drawingState: 'InProgress', currentRound: event.rank };
  } else if (event.event === DRAWING_BOARD_EVENTS.WINNER) {
    update = {
      currentRound: event.rank,
      lastWinnerName: (event.user && event.user.displayName) || 'No Winner'
    };
  } else if (event.event === DRAWING_BOARD_EVENTS.CLAIMED) {
    update = {
      currentRound: event.rank,
      lastWinnerName: (event.user && event.user.displayName) || 'No Winner'
    };

    if (commonDrawing) {
      update.drawingWinners = {
        drawingWinners:
          (commonDrawing.drawingWinners &&
            commonDrawing.drawingWinners.drawingWinners) ||
          []
      };
      update.drawingWinners.drawingWinners.push({
        rank: event.rank,
        claimed: true,
        user: { userId: event.user.userId, displayName: event.user.displayName }
      });
    }
  }

  if (update) {
    update.lastEvent = event.event;
    update.lastEventTimeMs = Date.now();

    if (commonDrawing) {
      Object.assign(commonDrawing, update);
    }
    if (myDrawing) {
      Object.assign(myDrawing, update);
    }
  }

  updateDrawingsInCache(client, data);
  return commonDrawing;
};

//  ---------------------------------
// drawing:
//   description: "Dinner for two at FIRE ($130) and $50 Free Play"
//   drawTime: "2019-03-29 14:47:00"
//   drawingState: "Upcoming"
//   groupId: null
//   id: "25"
//   name: "Free play & FIRE dinner"
//   winningPlaces: {winningPlaces: Array(3), __typename: "WinningPlaces"}
//   __typename: "Drawing"

//  ---------------------------------
//  event:
//    "created"
//    "updated"
//    "deleted"
//    "refunded"
//    "addTickets"
//    "addFreeTicket"
//    "notifyStart"
//    "start"
//    "round"
//    "winner"
//    "claimed"
//    "end"
